import React, { useCallback, useEffect, useState } from 'react';
import IntegrationCard from './IntegrationCard';
import {
  error as errorToast,
  success as successToast,
} from 'helpers/toaster.js';
import { Col } from 'react-bootstrap';
import {
  getAvailableIntegrations,
  postNewIntegration,
  updateIntegration,
  deleteIntegration,
  patchIntegration,
  getUserIntegrations,
} from 'services/integrationService';
import Styles from './Integrations.module.scss';
import { useSelector } from 'react-redux';
import UpgradeModal from 'Containers/Modal/UpgradeModal/UpgradeModal';
import IntegrationPlatformList from './IntegrationPlatformList';
import IntegrationFormModal from './IntegrationFormModal';
import ErrorModal from 'Components/Modal/CampaignErrorModal/CampaignErrorModal';
import { getRequest } from 'helpers/axios';

const Integrations = ({ projectId }) => {
  // State management
  const [availableIntegrations, setAvailableIntegrations] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [updatedIntegration, setUpdatedIntegration] = useState(null);
  const [tabKey, setTabKey] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const [errorModal, setErrorModal] = useState(false);

  // Get user data from Redux store
  const { user } = useSelector((state) => state.auth);
  const { subscriptions } = user || {};
  const { current_subscription: currentSubscription } = subscriptions ?? {};
  const isPremiumUser = currentSubscription?.plan_name !== 'Basic Plan';

  // Fetch data on component mount
  useEffect(() => {
    fetchAvailableIntegrations();
    fetchUserIntegrations();
  }, [projectId]);

  const fetchAvailableIntegrations = useCallback(async () => {
    try {
      const integrations = await getAvailableIntegrations();

      setAvailableIntegrations(integrations?.data);
    } catch (error) {
      errorToast(error);
    }
  }, []);

  const fetchUserIntegrations = useCallback(async () => {
    try {
      const { data } = await getUserIntegrations(projectId);

      setIntegrations(data?.platform_integrations);
    } catch (error) {
      errorToast(error);
    }
  }, []);

  // Event handlers
  const handleSocialSuccess = () => {
    setShowFormModal(false);
    fetchUserIntegrations();
  };

  const handlePlatformSelect = async (type) => {
    try {
      // Check if the integration type requires a limit check
      const limitCheckTypes = ['wordpress', 'wix', 'webflow'];

      if (limitCheckTypes.includes(type.toLowerCase())) {
        const limitResponse = await getRequest(
          'check_limits?type=platform_integration',
        );

        if (!limitResponse.data.success) {
          if (currentSubscription?.plan_name === 'Basic Plan') {
            setShowUpgradeModal(true);
            return;
          } else {
            setErrorDetails({
              message:
                limitResponse.data.message ||
                'You have reached your platform integration limit',
              link: limitResponse.data.link || '',
            });
            setErrorModal(true);
            return;
          }
        }
      }

      // If limit check passes or not required, proceed with the integration
      setTabKey(type);
      setShowFormModal(true);
    } catch (error) {
      setErrorDetails({
        message:
          error.response?.data?.error ||
          'You have reached your platform integration limit',
        link: 'https://appsumo.com/products/vocable-ai',
      });
      if (currentSubscription?.plan_name === 'Basic Plan') {
        setShowUpgradeModal(true);
      } else {
        if (error.response?.data?.limit_error === true) {
          setErrorModal(true);
        } else {
          errorToast(error.response?.data?.error);
        }
      }
    }
  };

  const handleAddIntegration = async (type, payload) => {
    try {
      const res = await postNewIntegration(type, payload);
      fetchUserIntegrations();
      successToast('Integration successfully added!');
      setShowFormModal(false);
    } catch (error) {
      if (currentSubscription?.is_appsumo) {
        setErrorDetails({
          message: error?.response?.data?.error || 'Something went wrong',
          link: 'https://appsumo.com/products/vocable-ai',
        });
        if (error.response?.data?.limit_error === true) {
          setErrorModal(true);
        } else {
          errorToast(error.response?.data?.error);
        }
      } else {
        errorToast(error);
      }
    }
  };

  const handleActive = async (integrationToChange) => {
    try {
      await patchIntegration(
        integrationToChange.type,
        integrationToChange.id,
        'active',
        {
          is_active: !integrationToChange.is_active,
        },
      );
      successToast(
        `Integration ${integrationToChange.is_active ? 'Inactive' : 'Active'}!`,
      );
      fetchUserIntegrations();
    } catch (error) {
      errorToast(error);
    }
  };

  const handleUpdate = (integrationToUpdate) => {
    setUpdatedIntegration(integrationToUpdate);
    setShowFormModal(true);
  };

  const handleUpdateIntegration = useCallback(
    async (type, id, payload) => {
      try {
        await updateIntegration(type, id, payload);
        successToast('Integration successfully updated!');
        fetchUserIntegrations();
        setUpdatedIntegration(null);
        setShowFormModal(false);
      } catch (error) {
        errorToast(error);
      }
    },
    [fetchUserIntegrations],
  );

  const handleDelete = async (integrationToDelete) => {
    try {
      await deleteIntegration(integrationToDelete.type, integrationToDelete.id);
      setIntegrations((prevIntegrations) =>
        prevIntegrations.filter(
          (integration) => integration.id !== integrationToDelete.id,
        ),
      );
      successToast('Integration successfully deleted!');
    } catch (error) {
      errorToast(error);
    }
  };

  const handleFormSubmit = useCallback(
    (type, values) => {
      const payload = { integration: { ...values, project_id: projectId } };
      if (updatedIntegration) {
        handleUpdateIntegration(type, updatedIntegration.id, payload);
      } else {
        handleAddIntegration(type, payload);
      }
    },
    [
      projectId,
      updatedIntegration,
      handleAddIntegration,
      handleUpdateIntegration,
    ],
  );

  const handleFloatingButtonClick = () => {
    if (!isPremiumUser && projectId !== null) {
      setShowUpgradeModal(true);
    } else {
      setShowFormModal(true);
    }
  };

  const handleUpdateButtonClick = useCallback(
    (integrationToUpdate) => {
      if (!isPremiumUser) {
        setShowUpgradeModal(true);
      } else {
        handleUpdate(integrationToUpdate);
      }
    },
    [isPremiumUser, handleUpdate],
  );

  const closeFormModal = useCallback(() => {
    setUpdatedIntegration(null);
    setShowFormModal(false);
  }, []);

  // Render empty state
  const renderEmptyState = () => (
    <div className={Styles.Integrations__emptyState}>
      <div>
        <h4>{`Hey, ${user.name}!`}</h4>
        <p>
          You still haven't configured any integrations.
          <br />
          Add your first integration:
        </p>
      </div>
      <IntegrationPlatformList
        availableIntegrations={availableIntegrations}
        onPlatformSelect={handlePlatformSelect}
        onSocialSuccess={handleSocialSuccess}
      />
    </div>
  );

  // Render integration cards
  const renderIntegrationCards = () => (
    <div className={Styles.Integrations__cards}>
      <h4 style={{ marginBottom: '1rem' }}>Connect a New Channel</h4>
      {/* platform integration list */}
      <IntegrationPlatformList
        availableIntegrations={availableIntegrations}
        onPlatformSelect={handlePlatformSelect}
        onSocialSuccess={handleSocialSuccess}
      />

      {/* platform integrated  */}
      <h4 style={{ marginBottom: '1rem', marginTop: '2rem' }}>
        Connected Integrations
      </h4>
      <div className={'g-3 ' + Styles.integrationGrid}>
        {integrations?.map((integration) => (
          <Col key={integration.name}>
            <IntegrationCard
              integration={integration}
              handleActive={handleActive}
              handleUpdate={
                currentSubscription?.plan_name === 'Basic Plan'
                  ? () => setShowUpgradeModal(true)
                  : handleUpdate
              }
              handleDelete={handleDelete}
            />
          </Col>
        ))}
      </div>
    </div>
  );

  return (
    <section className={Styles.Integrations}>
      {/* {isLoading && <div className={Styles.loadingOverlay}>Loading...</div>} */}

      {integrations?.length <= 0
        ? renderEmptyState()
        : renderIntegrationCards()}

      <IntegrationFormModal
        show={showFormModal}
        onHide={closeFormModal}
        availableIntegrations={availableIntegrations}
        tabKey={tabKey}
        updatedIntegration={updatedIntegration}
        onSubmit={handleFormSubmit}
      />

      {showUpgradeModal && (
        <UpgradeModal
          show={showUpgradeModal}
          onHide={() => setShowUpgradeModal(false)}
        />
      )}

      {errorModal && (
        <ErrorModal
          show={errorModal}
          onHide={() => setErrorModal(false)}
          errorMessage={errorDetails?.message}
          errorLink={errorDetails?.link}
        />
      )}
    </section>
  );
};

export default Integrations;
